import { customizeToast } from "./Toast";

const FORM_SELECTOR = "[data-newsletter-form]";
const SUBMIT_SELECTOR = "[data-newsletter-submit]";
const MODAL_SELECTOR = "[data-newsletter-modal]";
const OPEN_MODAL_SELECTOR = "[data-newsletter-open-modal]";
const CLOSE_MODAL_SELECTOR = "[data-newsletter-modal-close]";
const INLINE_EMAIL_SELECTOR = "[data-newsletter-email-source]";
const MODAL_EMAIL_SELECTOR = "[data-newsletter-email-target]";
const INLINE_ERROR_SELECTOR = "[data-newsletter-inline-error]";
const FIELD_INVALID_CLASS = "form__field--invalid";

/**
 * Serialize a form's named inputs into a plain object.
 * @param {HTMLFormElement} form
 * @returns {Record<string, string>}
 */
const serializeForm = (form) => {
	const data = {};
	new FormData(form).forEach((value, key) => {
		data[key] = String(value).trim();
	});
	return data;
};

/**
 * Toggle the form's loading state.
 * @param {HTMLFormElement} form
 * @param {boolean} loading
 */
const setLoading = (form, loading) => {
	const btn = form.querySelector(SUBMIT_SELECTOR);
	if (!btn) return;

	btn.disabled = loading;
	btn.classList.toggle("is-loading", loading);
	if (loading) {
		btn.setAttribute("aria-busy", "true");
	} else {
		btn.removeAttribute("aria-busy");
	}
};

/**
 * @param {string} message
 * @param {Element | null | undefined} [errorEl] — Si omis : premier `[data-newsletter-inline-error]` (footer). Si `null` : aucun élément (ex. bloc sans champ e-mail).
 */
const setInlineError = (message, errorEl) => {
	let el;
	if (arguments.length < 2) {
		el = document.querySelector(INLINE_ERROR_SELECTOR);
	} else {
		el = errorEl;
	}
	if (!el) return;
	el.textContent = message;
	el.hidden = message === "";
};

const syncFloatingLabel = (input) => {
	if (!input) return;

	const container = input.closest(".has-floating-label");
	if (!container) return;

	container.classList.toggle("label-is-floating", !!input.value || input === document.activeElement);
};

const clearFieldValidity = (form) => {
	form.querySelectorAll(`.${FIELD_INVALID_CLASS}`).forEach((el) => el.classList.remove(FIELD_INVALID_CLASS));
};

const markFieldInvalid = (input) => {
	const field = input?.closest(".form__field");
	if (field) field.classList.add(FIELD_INVALID_CLASS);
};

const openModal = () => {
	const modal = document.querySelector(MODAL_SELECTOR);
	if (!modal) return;

	modal.classList.add("is-visible");
	modal.setAttribute("aria-hidden", "false");
	modal.querySelector(".modal__dialog")?.focus();
};

const closeModal = () => {
	const modal = document.querySelector(MODAL_SELECTOR);
	if (!modal) return;

	modal.classList.remove("is-visible");
	modal.setAttribute("aria-hidden", "true");
};

const copyInlineEmailToModal = (sourceInput) => {
	const modalEmail = document.querySelector(MODAL_EMAIL_SELECTOR);
	if (!sourceInput || !modalEmail) return;

	modalEmail.value = sourceInput.value.trim();
	syncFloatingLabel(modalEmail);
};

/**
 * @param {HTMLElement | null | undefined} openButton Bouton « S'inscrire » du formulaire inline concerné (footer ou bloc).
 */
const handleOpenModal = (openButton) => {
	const form = openButton?.closest(".newsletter-form");
	const inlineEmail = form?.querySelector(INLINE_EMAIL_SELECTOR);
	const errorEl = form?.querySelector(INLINE_ERROR_SELECTOR);

	if (!inlineEmail) {
		openModal();
		document.querySelector(MODAL_EMAIL_SELECTOR)?.focus();
		return;
	}

	const email = inlineEmail.value.trim();
	if (email === "") {
		setInlineError("Veuillez saisir votre adresse e-mail.", errorEl);
		inlineEmail.focus();
		return;
	}

	if (!inlineEmail.checkValidity()) {
		setInlineError("Veuillez saisir une adresse e-mail valide.", errorEl);
		inlineEmail.focus();
		return;
	}

	setInlineError("", errorEl);
	copyInlineEmailToModal(inlineEmail);
	openModal();
	document.querySelector(MODAL_EMAIL_SELECTOR)?.focus();
};

/**
 * Submit the newsletter subscription via WordPress AJAX.
 * @param {HTMLFormElement} form
 */
const submitSubscription = async (form) => {
	if (form.querySelector(SUBMIT_SELECTOR)?.classList.contains("is-loading")) return;

	const fields = serializeForm(form);

	clearFieldValidity(form);

	const invalidInputs = [];
	if (!fields.first_name) invalidInputs.push(form.querySelector("[name='first_name']"));
	if (!fields.last_name) invalidInputs.push(form.querySelector("[name='last_name']"));
	if (!fields.email) invalidInputs.push(form.querySelector("[name='email']"));

	if (invalidInputs.length) {
		invalidInputs.forEach((input) => markFieldInvalid(input));
		invalidInputs[0]?.focus();
		return;
	}

	setLoading(form, true);

	try {
		const body = new URLSearchParams();
		body.set("action", "schilliger_global");
		body.set("function", "brevo_subscribe");
		body.set("data[nonce]", fields.nonce ?? "");
		body.set("data[email]", fields.email);
		body.set("data[first_name]", fields.first_name ?? "");
		body.set("data[last_name]", fields.last_name ?? "");

		const response = await fetch(themeVars.ajaxUrl, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: body.toString(),
		});

		const json = await response.json();

		if (json.success) {
			customizeToast(true, json.data?.message ?? "Inscription réussie !");
			form.reset();
			clearFieldValidity(form);
			form.querySelectorAll("input, textarea, select").forEach(syncFloatingLabel);

			if (form.closest(MODAL_SELECTOR)) {
				closeModal();
			}
		} else {
			const message = json.data?.message ?? "Une erreur est survenue.";
			customizeToast(false, "", message);
		}
	} catch {
		const message = "Une erreur réseau est survenue. Veuillez réessayer.";
		customizeToast(false, "", message);
	} finally {
		setLoading(form, false);
	}
};

const bindEvents = () => {
	document.addEventListener("click", (event) => {
		const openButton = event.target.closest(OPEN_MODAL_SELECTOR);
		if (openButton) {
			event.preventDefault();
			handleOpenModal(openButton);
			return;
		}

		if (event.target.closest(CLOSE_MODAL_SELECTOR)) {
			event.preventDefault();
			closeModal();
			return;
		}

		const modal = document.querySelector(MODAL_SELECTOR);
		if (modal && modal.classList.contains("is-visible") && event.target === modal) {
			closeModal();
		}
	});

	document.addEventListener("keydown", (event) => {
		if (event.key !== "Escape") return;

		const modal = document.querySelector(MODAL_SELECTOR);
		if (!modal || !modal.classList.contains("is-visible")) return;

		closeModal();
	});

	document.addEventListener("submit", (event) => {
		const form = event.target.closest(FORM_SELECTOR);
		if (!form) return;

		event.preventDefault();
		submitSubscription(form);
	});

	document.addEventListener("submit", (event) => {
		const form = event.target;
		if (!(form instanceof HTMLFormElement) || !form.classList.contains("newsletter-form")) return;
		const openBtn = form.querySelector(OPEN_MODAL_SELECTOR);
		if (!openBtn) return;
		event.preventDefault();
		handleOpenModal(openBtn);
	});

	document.addEventListener("input", (event) => {
		const target = event.target;

		if (target.matches(INLINE_EMAIL_SELECTOR)) {
			if (target.value.trim() === "") return;
			const err = target.closest(".newsletter-form")?.querySelector(INLINE_ERROR_SELECTOR);
			if (err) {
				setInlineError("", err);
			}
		}

		if (target.closest(FORM_SELECTOR)) {
			target.closest(".form__field")?.classList.remove(FIELD_INVALID_CLASS);
		}
	});
};

const NewsletterForm = {
	init() {
		bindEvents();
	},
};

export { NewsletterForm };
